/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import React from 'react';
import FOTable from 'components/foTable/FOTable';
import { useTranslation } from 'react-i18next';
import TableFilter from 'components/foTable/foTableFilter/TableFilter';
import { useScreenSize } from 'shared/theming/useScreenSize';
import { useTheming } from 'shared/theming/useTheming';
import useRemoveInvitationFromOrg from 'redux/features/invitation/remove/useRemoveInvitationFromOrg';
import RemoveInvitationDialog from 'components/invitation/removeInvitationDialog';
import UseResendInvitation from 'redux/features/invitation/resend/useResendInvitation';
import ResendInvitationDialog from 'components/invitation/resendInvitationDialog';
import { computeCombinedCellEntries } from './InvitationsTable.helper';
import useInvitationsTable from './useInvitationsTable';

export default function InvitationsTable({ currentOrganization }) {
  const { isMediumOrBigger } = useScreenSize();
  const { t } = useTranslation();
  const theming = useTheming();

  const {
    tableConfiguration,
    rows,
    columns,
    data,
    totalCount,
    loading,
    pageNo,
    pageSize,
    filter,
    setPageNo,
    getInvitations,
    setPageSize,
    setFilter,
    getSortOrderForColumn,
    onSortOrderClick,
    handleSelectAll,
    handleSelectRow,
    isAllSelected,
    isRowSelected,
  } = useInvitationsTable(theming);

  const {
    removeDialog,
    invitationId,
    onToggleRemoveDialog,
    onRemoveInvitationConfirmation,
  } = useRemoveInvitationFromOrg(currentOrganization?.id, getInvitations);

  const {
    resendDialog,
    invitationId: resendInvitationId,
    onToggleResendDialog,
    onResendInvitationConfirmation,
  } = UseResendInvitation(currentOrganization?.id, getInvitations);

  return (
    <>
      <section
        data-testId="invitations-table"
        aria-labelledby="invitations-table"
        aria-describedby="invitations-table"
        style={{ overflowX: 'auto' }}
      >
        <TableFilter
          isLargeScreen={isMediumOrBigger}
          filter={filter}
          setFilter={setFilter}
          updatePage={setPageNo}
          isAddressBookPage={false}
          columnsConfiguration={tableConfiguration?.columns}
        />
        <FOTable
          columns={columns}
          rows={rows}
          data={data}
          totalCount={totalCount}
          loading={loading}
          page={pageNo}
          filter={filter}
          pageSize={pageSize}
          tableConfiguration={tableConfiguration}
          updatePageAction={setPageNo}
          updatePageSizeAction={setPageSize}
          isAllSelected={isAllSelected}
          handleSelectAll={handleSelectAll}
          getSortOrderForColumn={getSortOrderForColumn}
          onClickColumn={onSortOrderClick}
          isRowSelected={isRowSelected}
          handleSelectRow={handleSelectRow}
          setFilter={setFilter}
          computeCombinedCellEntries={computeCombinedCellEntries}
          emptyTableSuggestion={t('user-management.invite.new.user')}
          emptyTableWarningMessage={t('user-management.no.pending.invitations')}
        />
      </section>

      {removeDialog.open && (
        <RemoveInvitationDialog
          invitationId={invitationId}
          dialog={removeDialog}
          toggleDialog={onToggleRemoveDialog}
          onSubmit={onRemoveInvitationConfirmation}
        />
      )}

      {resendDialog.open && (
        <ResendInvitationDialog
          invitationId={resendInvitationId}
          dialog={resendDialog}
          toggleDialog={onToggleResendDialog}
          onSubmit={onResendInvitationConfirmation}
        />
      )}
    </>
  );
}
